
import React, { useState } from 'react';
import { Comment } from '../../services/socialService';
import { Send, X } from 'lucide-react';

interface ReplyModalProps {
  isOpen: boolean;
  onClose: () => void;
  comment: Comment;
  onSend: (reply: string) => void;
  isLoading?: boolean;
}

const ReplyModal: React.FC<ReplyModalProps> = ({ isOpen, onClose, comment, onSend, isLoading = false }) => {
  const [reply, setReply] = useState('');

  const handleSend = () => {
    if (reply.trim() && !isLoading) {
      onSend(reply.trim());
      setReply('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSend();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-slate-500/30 dark:bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 backdrop-blur-xl rounded-2xl p-6 w-full max-w-lg shadow-2xl border border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">
            Reply to {comment.author}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="mb-4 p-4 bg-slate-100 dark:bg-slate-700/50 rounded-xl border border-slate-200 dark:border-slate-600">
          <p className="text-sm text-slate-700 dark:text-slate-300 mb-2 font-medium">Original comment:</p>
          <p className="text-slate-900 dark:text-slate-100 italic">"{comment.text}"</p>
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-2">
            On: {comment.post_title}
          </p>
        </div>
        
        <div className="mb-4">
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={handleKeyPress}
            className="w-full p-4 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500"
            rows={4}
            placeholder="Write your reply... (Ctrl+Enter to send)"
            disabled={isLoading}
          />
          <p className="text-xs text-slate-600 dark:text-slate-400 mt-2">
            Tip: Press Ctrl+Enter to send quickly
          </p>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-slate-700 dark:text-slate-200 bg-slate-200 dark:bg-slate-600 rounded-xl hover:bg-slate-300 dark:hover:bg-slate-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={!reply.trim() || isLoading}
            className="px-6 py-2 text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Posting reply...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Send Reply</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReplyModal;
